/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, { PropsWithChildren } from "react";
import InfoIcon from "@mui/icons-material/Info";

export enum BeamSectionAlertType {
  INFO = 'info',
  ERROR = 'error',
}

interface BeamSectionAlertProps {
  style?: React.CSSProperties;
  type?: BeamSectionAlertType;
}

/**
 * Section alert notifications are inline alerts that should be used to highlight important information.
 * @see https://www.figma.com/design/UrwKV2u8AtuLl7AO2QgNhE/Beam-2---Components-%26-Styles?node-id=32940-85579&m=dev
 * @param props Alert properties. Notably, the type of alert (INFO or ERROR).
 */
const BeamSectionAlert = (props: PropsWithChildren<BeamSectionAlertProps>) => {
  // Define color based on type; default to INFO.
  let backgroundColor;
  let iconColor;
  switch (props.type ?? BeamSectionAlertType.INFO) {
    case BeamSectionAlertType.ERROR:
      backgroundColor = 'rgba(250, 114, 114, 0.1)';
      iconColor = 'rgba(250, 114, 114, 1)';
      break;
    case BeamSectionAlertType.INFO:
      backgroundColor = 'rgba(100, 206, 251, 0.1)';
      iconColor = '#64CEFB';
      break;
    default:
      // Display an obnoxious pink color if the type is not recognized
      backgroundColor = 'rgba(255, 0, 255, 0.1)';
      iconColor = '#FF00FF';
  }

  return (
    <div
      style={{
        borderRadius: '8px',
        backgroundColor,
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        ...props.style,
      }}
    >
      <InfoIcon sx={{ color: iconColor, width: 20, height: 20 }} />
      <div style={{ flex: '1', marginLeft: '8px', color: 'white' }}>{props.children}</div>
    </div>
  );
};

export default BeamSectionAlert;
